import type { ApiErrorPayload } from '@/types/api';
import { API_AUTH_SCHEME, API_BASE_URL } from '@/config/env';

export class ApiError extends Error {
  status: number;
  code?: string;
  errors?: Record<string, string[]>;
  requestId?: string;
  path?: string;

  constructor(message: string, options: {
    status: number;
    code?: string;
    errors?: Record<string, string[]>;
    requestId?: string;
    path?: string;
  }) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.errors = options.errors;
    this.requestId = options.requestId;
    this.path = options.path;
  }
}

export type ApiRequestInit = Omit<RequestInit, 'body'> & {
  token?: string;
  body?: BodyInit | Record<string, unknown> | unknown[] | null;
};

const JSON_HEADER = 'application/json';

const buildUrl = (path: string) => {
  if (path.startsWith('http')) {
    return path;
  }

  // Same-origin API calls must pass through the server proxy so it can attach
  // the HttpOnly authentication cookie without exposing the token to the browser.
  if (path.startsWith('/api/')) {
    return path;
  }

  const baseUrl = API_BASE_URL.trim().replace(/\/$/, '');
  if (!baseUrl) {
    return path.startsWith('/') ? path : `/${path}`;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (baseUrl.endsWith('/api') && normalizedPath.startsWith('/api')) {
    const trimmedPath = normalizedPath.replace(/^\/api/, '') || '/';
    return `${baseUrl}${trimmedPath}`;
  }

  return `${baseUrl}${normalizedPath}`;
};

const readBody = async <T,>(response: Response) => {
  const contentType = response.headers.get('content-type') ?? '';
  const text = await response.text();
  if (!text) {
    return { payload: undefined as T, text };
  }

  const trimmed = text.trim();
  const looksLikeJson = contentType.includes(JSON_HEADER) || trimmed.startsWith('{') || trimmed.startsWith('[');
  if (looksLikeJson) {
    try {
      return { payload: JSON.parse(text) as T, text };
    } catch {
      return { payload: undefined as T, text };
    }
  }

  return { payload: undefined as T, text };
};

const getRequestId = (response: Response) => {
  return (
    response.headers.get('x-request-id') ??
    response.headers.get('x-trace-id') ??
    undefined
  );
};

export const apiRequest = async <T,>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> => {
  const { token, headers, ...rest } = init;
  const resolvedToken = token;
  const authScheme = (API_AUTH_SCHEME || 'Bearer').trim();

  const isJsonBody = rest.body && typeof rest.body === 'object' && !(rest.body instanceof FormData);
  const finalBody = isJsonBody ? JSON.stringify(rest.body) : rest.body;

  const response = await fetch(buildUrl(path), {
    ...rest,
    credentials: 'same-origin',
    body: finalBody as BodyInit,
    headers: {
      Accept: JSON_HEADER,
      ...(isJsonBody ? { 'Content-Type': JSON_HEADER } : {}),
      ...(resolvedToken ? { Authorization: `${authScheme} ${resolvedToken}` } : {}),
      ...headers,
    },
  });

  const requestId = getRequestId(response);

  if (!response.ok) {
    const { payload, text } = await readBody<ApiErrorPayload>(response);
    const fallbackMessage = response.statusText || 'Error inesperado.';
    const baseMessage = payload?.message ?? (text && !text.trim().startsWith('<') ? text : fallbackMessage);
    const validationDetails = payload?.errors
      ? ` Detalles: ${Object.entries(payload.errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join(' | ')}`
      : '';
    const message = `${baseMessage}${validationDetails} (Path: ${path})`;

    throw new ApiError(message, {
      status: response.status,
      code: payload?.code,
      errors: payload?.errors,
      requestId: payload?.request_id ?? requestId,
      path: payload?.path,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const { payload } = await readBody<T>(response);
  return payload as T;
};
